import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import sellerCustomerTiersService from '../../services/SellerApis/sellerCustomerTiersService';

// Async thunks
export const fetchSellerTiers = createAsyncThunk(
  'sellerCustomerTiers/fetchTiers',
  async (_, { rejectWithValue }) => {
    try {
      return await sellerCustomerTiersService.getTiers();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createSellerTier = createAsyncThunk(
  'sellerCustomerTiers/createTier',
  async (tierData, { rejectWithValue }) => {
    try {
      return await sellerCustomerTiersService.createTier(tierData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateSellerTier = createAsyncThunk(
  'sellerCustomerTiers/updateTier',
  async ({ tierId, tierData }, { rejectWithValue }) => {
    try {
      return await sellerCustomerTiersService.updateTier(tierId, tierData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteSellerTier = createAsyncThunk(
  'sellerCustomerTiers/deleteTier',
  async (tierId, { rejectWithValue }) => {
    try {
      await sellerCustomerTiersService.deleteTier(tierId);
      return tierId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const toggleSellerTierStatus = createAsyncThunk(
  'sellerCustomerTiers/toggleTierStatus',
  async (tierId, { rejectWithValue }) => {
    try {
      return await sellerCustomerTiersService.toggleTierStatus(tierId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  tiers: [],
  globalSettings: true,
  canCreate: false,
  loading: false,
  error: null
};

const sellerCustomerTiersSlice = createSlice({
  name: 'sellerCustomerTiers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetTiers: (state) => {
      state.tiers = [];
      state.globalSettings = true;
      state.canCreate = false;
    }
  },
  extraReducers: (builder) => {
    // Fetch tiers
    builder
      .addCase(fetchSellerTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSellerTiers.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data || {};
        state.tiers = data.tiers || data.data || [];
        state.globalSettings = data.global_settings || false;
        state.canCreate = data.can_create || false;
      })
      .addCase(fetchSellerTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create tier
    builder
      .addCase(createSellerTier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSellerTier.fulfilled, (state, action) => {
        state.loading = false;
        const newTier = action.payload?.data;
        if (newTier) {
          state.tiers.unshift(newTier);
        }
      })
      .addCase(createSellerTier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update tier
    builder
      .addCase(updateSellerTier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSellerTier.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTier = action.payload?.data;
        if (updatedTier) {
          const index = state.tiers.findIndex(t => t.id === updatedTier.id);
          if (index !== -1) {
            state.tiers[index] = updatedTier;
          }
        }
      })
      .addCase(updateSellerTier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete tier
    builder
      .addCase(deleteSellerTier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSellerTier.fulfilled, (state, action) => {
        state.loading = false;
        state.tiers = state.tiers.filter(t => t.id !== action.payload);
      })
      .addCase(deleteSellerTier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Toggle tier status
    builder
      .addCase(toggleSellerTierStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleSellerTierStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTier = action.payload?.data;
        if (updatedTier) {
          const index = state.tiers.findIndex(t => t.id === updatedTier.id);
          if (index !== -1) {
            state.tiers[index] = updatedTier;
          }
        }
      })
      .addCase(toggleSellerTierStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, resetTiers } = sellerCustomerTiersSlice.actions;
export default sellerCustomerTiersSlice.reducer;